import axios from 'axios';
import constants from 'config/constants';
import { getGlobalCurrentBatchUtils } from 'utils/commonFunc';

const APIS = {
  feeStructure: '/api/admin/transport-fee-structure', // POST, PUT / GET /school/{schoolId}/batch/{batchId} / DELETE /{id}
  area: '/api/admin/transport-area', // POST, PUT / GET /structure/{transportFeeStructId} / DELETE /{id}
  studentTransport: '/api/student-transport', // POST / GET /area/{transportAreaId} / DELETE /{id}
};

const BASE = constants.API_BASE_URL;

export const GET_ALL_TRANSPORT_GROUPS = 'GET_ALL_TRANSPORT_GROUPS';
export const getAllTransportGroups = transportGroups => ({ type: GET_ALL_TRANSPORT_GROUPS, transportGroups });

export const GET_TRANSPORT_AREAS = 'GET_TRANSPORT_AREAS';
export const getTransportAreas = (transportAreas, transportFeeStructId) => ({
  type: GET_TRANSPORT_AREAS,
  transportAreas,
  transportFeeStructId,
});

export const GET_STUDENTS_FOR_TRANSPORT_AREA = 'GET_STUDENTS_FOR_TRANSPORT_AREA';
export const getStudentsForTransportArea = (students, transportAreaId) => ({
  type: GET_STUDENTS_FOR_TRANSPORT_AREA,
  students,
  transportAreaId,
});

// -------------------- Transport Fee Group (TransportFeeStructure) -------------------- //

export const fetchAllTransportGroupsService = data => async dispatch => {
  const { batchId = data?.batchId } = getGlobalCurrentBatchUtils();
  const { schoolId, token } = data;
  try {
    const res = await axios.get(
      `${BASE}${APIS.feeStructure}/school/${schoolId}/batch/${batchId}`,
      constants.setHeader(token)
    );
    dispatch(getAllTransportGroups(res.data));
    return res.data;
  } catch (err) {
    console.error('err-fetchAllTransportGroupsService', err);
  }
};

export const addTransportGroupService = data => async () => {
  const { token, postData } = data;
  return axios.post(`${BASE}${APIS.feeStructure}`, postData, constants.setHeader(token));
};

// Plain (non-thunk) variant of fetchAllTransportGroupsService, for read-only spots (e.g. a
// student's own profile) that just need the route list once without wiring up the transportFee
// reducer for it.
export const fetchAllTransportGroupsPlainService = async (schoolId, token) => {
  const { batchId } = getGlobalCurrentBatchUtils();
  const res = await axios.get(
    `${BASE}${APIS.feeStructure}/school/${schoolId}/batch/${batchId}`,
    constants.setHeader(token)
  );
  return res.data;
};

export const deleteTransportGroupService = data => async () => {
  const { transportFeeStructId, token } = data;
  return axios.delete(`${BASE}${APIS.feeStructure}/${transportFeeStructId}`, constants.setHeader(token));
};

// -------------------- Route / Stop (TransportArea) -------------------- //

export const fetchTransportAreasService = data => async dispatch => {
  const { transportFeeStructId, token } = data;
  try {
    const res = await axios.get(`${BASE}${APIS.area}/structure/${transportFeeStructId}`, constants.setHeader(token));
    dispatch(getTransportAreas(res.data, transportFeeStructId));
  } catch (err) {
    console.error('err-fetchTransportAreasService', err);
  }
};

export const addTransportAreaService = data => async () => {
  const { token, postData } = data;
  return axios.post(`${BASE}${APIS.area}`, postData, constants.setHeader(token));
};

export const updateTransportAreaService = data => async () => {
  const { token, postData } = data;
  return axios.put(`${BASE}${APIS.area}`, postData, constants.setHeader(token));
};

export const deleteTransportAreaService = data => async () => {
  const { transportAreaId, token } = data;
  return axios.delete(`${BASE}${APIS.area}/${transportAreaId}`, constants.setHeader(token));
};

export const fetchTransportAreaByIdService = async (transportAreaId, token) => {
  const res = await axios.get(`${BASE}${APIS.area}/${transportAreaId}`, constants.setHeader(token));
  return res.data;
};

// Plain (non-thunk) variant of fetchTransportAreasService, same rationale as above.
export const fetchTransportAreasPlainService = async (transportFeeStructId, token) => {
  const res = await axios.get(`${BASE}${APIS.area}/structure/${transportFeeStructId}`, constants.setHeader(token));
  return res.data;
};

// -------------------- Duplicate guards -------------------- //
// The backend saves whatever name it is given - two routes (or two stops on one route) can share a name -
// so Fees > Transport Fee and the Transport page both check here, against the live data, before saving.

// Names are compared the way an admin reads them: ignoring case and extra spaces.
export const normalizeName = name => (name || '').trim().replace(/\s+/g, ' ').toLowerCase();

// Items whose name contains `name`, or is contained in it ("Chennai" vs "Chennai route"), without being the
// same name. These are "probably the same thing" cases worth a confirmation rather than a refusal. Very short
// names are skipped so a stray letter doesn't match everything.
export const findSimilarNames = (name, items, getName = item => item) => {
  const target = normalizeName(name);
  if (target.length < 3) return [];
  return items.filter(item => {
    const other = normalizeName(getName(item));
    return other.length >= 3 && other !== target && (other.includes(target) || target.includes(other));
  });
};

// Shaped like an API error so the forms' formResMessage() shows the text as is.
const validationError = message => Object.assign(new Error(message), { response: { data: { details: message } } });

export const assertRouteNameFree = async ({ schoolId, batchId, description, token }) => {
  const res = await axios.get(
    `${BASE}${APIS.feeStructure}/school/${schoolId}/batch/${batchId}`,
    constants.setHeader(token)
  );
  const clash = (res.data || []).find(group => normalizeName(group.description) === normalizeName(description));
  if (clash) {
    const stops = await fetchTransportAreasPlainService(clash.transportFeeStructId, token);
    throw validationError(
      `A route named "${clash.description}" already exists for this academic year (${stops.length} stop(s)). Use that route instead of adding it again.`
    );
  }
};

// `rows` are the stops about to be saved to one route; `ignoreAreaId` is the stop being edited. A destination
// name may exist only once per academic year: students are assigned to one destination, so the same name on
// two routes would leave admins and teachers guessing which one is the real one. Needs schoolId + batchId to
// look at the other routes.
export const assertStopNamesFree = async ({ transportFeeStructId, rows, ignoreAreaId, schoolId, batchId, token }) => {
  const names = rows.map(row => normalizeName(row.areaName));
  const repeated = rows.find((row, inx) => names.indexOf(normalizeName(row.areaName)) !== inx);
  if (repeated) {
    throw validationError(`"${repeated.areaName.trim()}" is listed more than once.`);
  }
  const groups =
    schoolId && batchId
      ? (await axios.get(`${BASE}${APIS.feeStructure}/school/${schoolId}/batch/${batchId}`, constants.setHeader(token)))
          .data || []
      : [{ transportFeeStructId }];
  const groupIds = groups.some(group => group.transportFeeStructId === transportFeeStructId)
    ? groups
    : [...groups, { transportFeeStructId }];
  const perRoute = await Promise.all(
    groupIds.map(async group => ({
      group,
      areas: (
        await fetchTransportAreasPlainService(group.transportFeeStructId, token)
      ).filter(area => area.transportAreaId !== ignoreAreaId),
    }))
  );
  for (const row of rows) {
    for (const { group, areas } of perRoute) {
      if (areas.some(area => normalizeName(area.areaName) === normalizeName(row.areaName))) {
        throw validationError(
          group.transportFeeStructId === transportFeeStructId
            ? `"${row.areaName.trim()}" is already a destination on this route. Students are assigned to the existing one.`
            : `"${row.areaName.trim()}" is already a destination on the route "${
                group.description
              }". Use that one instead of adding it again.`
        );
      }
    }
  }
};

// -------------------- Assign students (StudentTransport) -------------------- //

export const fetchStudentsForTransportAreaService = data => async dispatch => {
  const { transportAreaId, token } = data;
  try {
    const res = await axios.get(`${BASE}${APIS.studentTransport}/area/${transportAreaId}`, constants.setHeader(token));
    dispatch(getStudentsForTransportArea(res.data, transportAreaId));
    return res.data;
  } catch (err) {
    console.error('err-fetchStudentsForTransportAreaService', err);
  }
};

// The backend only takes one StudentTransport row per call (no bulk endpoint like the generic
// fee module's assign-to-students), so assigning N students means N calls fired together.
export const saveAssignedStudentsForTransportAreaService = data => async () => {
  const { token, students, transportFeeStructId, transportAreaId, schoolId, batchId } = data;
  return Promise.all(
    students.map(student =>
      axios.post(
        `${BASE}${APIS.studentTransport}`,
        {
          studentId: student.studentId,
          transportFeeStructId,
          transportAreaId,
          calculationType: 'DISTANCE_BASED',
          schoolId,
          batchId,
        },
        constants.setHeader(token)
      )
    )
  );
};

export const deleteStudentTransportService = data => async () => {
  const { studentTransportId, token } = data;
  return axios.delete(`${BASE}${APIS.studentTransport}/${studentTransportId}`, constants.setHeader(token));
};

export const fetchStudentTransportByStudentService = async (studentId, token) => {
  const res = await axios.get(`${BASE}${APIS.studentTransport}/student/${studentId}`, constants.setHeader(token));
  return res.data;
};
